// frontend/src/components/scenariobuilder.jsx

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { Oval } from 'react-loader-spinner';
import 'react-toastify/dist/ReactToastify.css';

// ✅ Helper Function to Check Empty Values
const isEmpty = (value) => {
  return value == null || String(value).trim() === '';
};

export default function ScenarioBuilder() {
  const [clientId, setClientId]     = useState('');
  const [scenarios, setScenarios]   = useState([]);
  const [form, setForm]             = useState({ name: '', steps: '', expected: '' });
  const [editingId, setEditingId]   = useState(null);
  const [loading, setLoading]       = useState(false);
  const clientIdRef                 = useRef(); // 🧠 Ref for Auto Focus

  useEffect(() => {
    clientIdRef.current?.focus();
  }, []);

  const fetchScenarios = async () => {
    if (isEmpty(clientId)) {
      toast.error('Please enter Client ID first!');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get(`/api/scenarios?clientId=${clientId}`);
      setScenarios(data.scenarios);
      toast.success('Scenarios loaded!');
    } catch (error) {
      console.error('Error loading scenarios:', error);
      toast.error('Failed to load scenarios.');
    } finally {
      setLoading(false);
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async () => {
    // 1) Validate required fields
    if (isEmpty(clientId) || isEmpty(form.name) || isEmpty(form.expected)) {
      toast.error('Client ID, Name, and Expected fields are required!');
      return;
    }

    // 2) Ensure expected is a number
    const expectedStatusNumber = Number(form.expected);
    if (isNaN(expectedStatusNumber)) {
      toast.error('Expected Status must be a valid number!');
      return;
    }

    // 3) Build payload
    const payload = {
      clientId,
      name: form.name.trim(),
      method: 'POST',
      path: '/default',
      expectedStatus: expectedStatusNumber
    };

    setLoading(true);
    try {
      if (editingId) {
        await axios.put(`/api/scenarios/${editingId}`, payload);
        toast.success('Scenario updated!');
      } else {
        await axios.post('/api/scenarios', payload);
        toast.success('Scenario created!');
      }

      // Clear form & reset editing state
      setForm({ name: '', steps: '', expected: '' });
      setEditingId(null);

      // Refresh the list
      await fetchScenarios();
    } catch (err) {
      // ← Fix: catch (err) must include parentheses around “err”
      console.error('Error saving scenario (full Axios error):', err);

      if (err.response) {
        // Server returned a status outside 2xx
        console.error('→ Response status:', err.response.status);
        console.error('→ Response data:', err.response.data);
        toast.error(
          `Failed to save scenario: ${err.response.data.message || err.response.statusText}`
        );
      } else if (err.request) {
        // Request was sent but no response received
        console.error('→ No response received:', err.request);
        toast.error('No response from server when saving scenario.');
      } else {
        // Something went wrong in setting up the request
        console.error('→ Axios setup error:', err.message);
        toast.error(`Error saving scenario: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This scenario will be deleted!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel',
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await axios.delete(`/api/scenarios/${id}`, { data: { clientId } });
        toast.success('Scenario deleted!');
        fetchScenarios();
      } catch (error) {
        console.error('Error deleting scenario:', error);
        toast.error('Failed to delete scenario.');
      } finally {
        setLoading(false);
      }
    }
  };

  const onEdit = (scenario) => {
    setEditingId(scenario._id);
    setForm({
      name: scenario.name,
      steps: '', // Steps aren’t displayed/editable in this UI example
      expected: scenario.expectedStatus
    });
  };

  const onApprove = async (id, approved) => {
    try {
      setLoading(true);
      await axios.patch(`/api/scenarios/${id}/approve`, { clientId, approved });
      toast.success(approved ? 'Scenario approved!' : 'Scenario unapproved!');
      fetchScenarios();
    } catch (error) {
      console.error('Error updating approval:', error);
      toast.error('Failed to update approval.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ fontSize: '28px', marginBottom: '24px', color: '#2c3e50' }}>
        🚀 Test Scenario Builder
      </h2>

      {/* ─── Form ───────────────────────────────────────────── */}
      <div
        style={{
          marginBottom: 32,
          background: '#fff',
          padding: 24,
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
        }}
      >
        <h3 style={{ marginBottom: 20, fontSize: '20px', color: '#34495e' }}>
          {editingId ? '📝 Edit Scenario' : '➕ New Scenario'}
        </h3>

        <input
          ref={clientIdRef}
          name="clientId"
          placeholder="Client ID"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            marginBottom: 12,
            borderRadius: '8px',
            border: '1px solid #ccc'
          }}
          disabled={loading}
        />

        <input
          name="name"
          placeholder="Scenario Name"
          value={form.name}
          onChange={onChange}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            marginBottom: 12,
            borderRadius: '8px',
            border: '1px solid #ccc'
          }}
        />

        <textarea
          name="expected"
          placeholder="Expected Status (e.g., 200)"
          value={form.expected}
          onChange={onChange}
          rows={2}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            marginBottom: 12,
            borderRadius: '8px',
            border: '1px solid #ccc'
          }}
        />

        <div style={{ marginTop: 16 }}>
          <button
            onClick={onSubmit}
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: '#28a745',
              color: 'white',
              fontWeight: 'bold',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              marginRight: 12
            }}
          >
            {loading ? 'Processing...' : editingId ? 'Update' : 'Create'}
          </button>

          {editingId && (
            <button
              onClick={() => {
                setEditingId(null);
                setForm({ name: '', steps: '', expected: '' });
              }}
              style={{
                padding: '12px 24px',
                backgroundColor: '#6c757d',
                color: 'white',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* ─── Scenario List ──────────────────────────────────── */}
      <div
        style={{
          background: '#fff',
          padding: 24,
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
        }}
      >
        <h3 style={{ marginBottom: 16, fontSize: '20px', color: '#34495e' }}>
          📚 Existing Scenarios {loading && '(Loading...)'}
        </h3>

        {loading ? (
          <div style={{ margin: '20px 0', textAlign: 'center' }}>
            <Oval height={40} width={40} color="#4fa94d" visible={true} ariaLabel="oval-loading" />
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>Name</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>Approved</th>
                <th style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((s) => (
                <tr key={s._id}>
                  <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{s.name}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={s.approved}
                      onChange={(e) => onApprove(s._id, e.target.checked)}
                    />
                  </td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
                    <button
                      onClick={() => onEdit(s)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ffc107',
                        color: '#212529',
                        fontWeight: 'bold',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        marginRight: '8px'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onDelete(s._id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        fontWeight: 'bold',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
